import { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CustomEase } from 'gsap/CustomEase'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { useGSAP } from '@gsap/react'
import { WistiaPlayer, WistiaPlayerElement } from '@wistia/wistia-player-react'
import { isWistiaPlayerReady, playWistiaPlayer } from '~/utils/wistia'

if (typeof window !== 'undefined') {
  gsap.registerPlugin(useGSAP)
  gsap.registerPlugin(ScrollTrigger, CustomEase, ScrollToPlugin)
}

const LazyWistiaPlayer = ({ mediaId }: { mediaId: string }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    const target = containerRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="mobile-visual-asset" ref={containerRef}>
      {shouldLoad ? (
        <WistiaPlayer transparentLetterbox mediaId={mediaId} muted />
      ) : (
        <div className="mobile-visual-asset__placeholder" aria-hidden="true" />
      )}
    </div>
  )
}

const updateScrollingBulletsTimeline = (root: HTMLElement) => {
  const bullets = Array.from(
    root.querySelectorAll<HTMLDivElement>('.bullets .bullet'),
  )
  const bulletsContainer = root.querySelector<HTMLDivElement>('.bullets')
  const maskEl = root.querySelector<SVGRectElement>('.animated-mask')
  const timelineRoot = root.querySelector<HTMLDivElement>(
    '.vertical-progress-timeline',
  )
  const timeline = root.querySelector<HTMLDivElement>('.timeline')
  const timelineSvg = root.querySelector<SVGSVGElement>('.timeline svg')
  const timelineBasePath = root.querySelector<SVGPathElement>(
    '.timeline path:not([clip-path])',
  )
  const timelineProgressPath = root.querySelector<SVGPathElement>(
    '.timeline path[clip-path]',
  )
  const firstDot = bullets[0]?.querySelector<HTMLElement>('.dot')
  const lastDot =
    bullets[bullets.length - 1]?.querySelector<HTMLElement>('.dot')

  if (
    !firstDot ||
    !lastDot ||
    !bulletsContainer ||
    !timelineRoot ||
    !timeline ||
    !timelineSvg ||
    !timelineBasePath ||
    !timelineProgressPath ||
    !maskEl
  ) {
    return
  }

  const timelineRootRect = timelineRoot.getBoundingClientRect()
  const firstDotRect = firstDot.getBoundingClientRect()
  const lastDotRect = lastDot.getBoundingClientRect()
  const timelineLead =
    Number.parseFloat(getComputedStyle(bulletsContainer).paddingTop) || 0
  const firstDotCenter =
    firstDotRect.top - timelineRootRect.top + firstDotRect.height / 2
  const lastDotCenter =
    lastDotRect.top - timelineRootRect.top + lastDotRect.height / 2
  const timelineStart = Math.max(firstDotCenter - timelineLead, 0)
  const timelineHeight = Math.max(lastDotCenter - timelineStart, 1)

  timeline.style.top = `${timelineStart}px`
  timeline.style.height = `${timelineHeight}px`
  timelineSvg.setAttribute('height', `${timelineHeight}`)
  timelineSvg.setAttribute('viewBox', `0 0 3 ${timelineHeight}`)
  maskEl.setAttribute('y', '0')
  maskEl.setAttribute('height', `${timelineHeight}`)
  timelineBasePath.setAttribute('d', `M1.5 0L1.5 ${timelineHeight}`)
  timelineProgressPath.setAttribute('d', `M1.5 0L1.5 ${timelineHeight}`)
  timeline.dataset.ready = 'true'
}

export const ScrollingBullets = () => {
  const scrollingBullets = useRef<HTMLElement>(null)
  const [loadedDesktopVideoIndexes, setLoadedDesktopVideoIndexes] = useState<
    Set<number>
  >(() => new Set([0, 1]))
  const wistiaPlayerRef = useRef<(WistiaPlayerElement | null)[]>([
    null,
    null,
    null,
    null,
    null,
  ])

  const handleVideoCanPlay = (e: any, index: number) => {
    // Play the first video, all others pause
    if (index !== 0 && isWistiaPlayerReady(e.srcElement)) {
      e.srcElement.pause()
    }
  }

  useEffect(() => {
    const root = scrollingBullets.current
    if (!root) return

    const updateTimelineFrame = () => {
      requestAnimationFrame(() => updateScrollingBulletsTimeline(root))
    }

    updateTimelineFrame()
    document.fonts?.ready.then(updateTimelineFrame)
    window.addEventListener('resize', updateTimelineFrame)

    return () => {
      window.removeEventListener('resize', updateTimelineFrame)
    }
  }, [])

  useGSAP(
    () => {
      const mm = gsap.matchMedia()

      mm.add('(min-width: 1024px)', () => {
        const root = scrollingBullets.current
        if (!root) return

        const visualAssets = Array.from(
          root.querySelectorAll<HTMLDivElement>('.visual-asset'),
        )
        const bullets = Array.from(
          root.querySelectorAll<HTMLDivElement>('.bullets .bullet'),
        )
        const maskEl = root.querySelector<SVGRectElement>('.animated-mask')

        const updateTimelineHeight = () => {
          updateScrollingBulletsTimeline(root)
        }

        const handleTimelineResize = () => {
          updateTimelineHeight()
          ScrollTrigger.refresh()
        }

        const handleTimelineResizeFrame = () => {
          requestAnimationFrame(handleTimelineResize)
        }

        const animationBreaks = [0, 0.28, 0.49, 0.69, 0.89, 1]
        let currentStage = -1

        const clampStage = (stage: number) =>
          Math.max(0, Math.min(stage, visualAssets.length - 1))

        const resolveStage = (progress: number) => {
          for (let i = 1; i < animationBreaks.length; i += 1) {
            if (progress < animationBreaks[i]) {
              return i - 1
            }
          }
          return animationBreaks.length - 2
        }

        const applyStage = (stage: number) => {
          const safeStage = clampStage(stage)
          setLoadedDesktopVideoIndexes((currentIndexes) => {
            const nextIndexes = new Set(currentIndexes)
            nextIndexes.add(safeStage)
            if (safeStage + 1 < visualAssets.length) {
              nextIndexes.add(safeStage + 1)
            }
            return nextIndexes
          })
          visualAssets.forEach((asset, idx) => {
            asset.classList.toggle('hidden', idx !== safeStage)
          })
          wistiaPlayerRef.current.forEach((player, idx) => {
            if (!isWistiaPlayerReady(player)) return
            if (idx === safeStage) {
              //play the video when its in view
              playWistiaPlayer(player)
            } else {
              player.pause()
            }
          })
          bullets.forEach((bullet, idx) => {
            bullet.classList.toggle('inactive', idx !== safeStage)
            bullet.classList.toggle('passed', idx === safeStage)
          })
          currentStage = safeStage
        }

        applyStage(0)
        updateTimelineHeight()
        document.fonts?.ready.then(handleTimelineResizeFrame)

        const triggerConfig = {
          trigger: '.scrolling-bullets .row',
          start: 'top 70%',
          end: 'bottom bottom-=125px',
        }

        const progressTween = maskEl
          ? gsap.fromTo(
              maskEl,
              { scaleY: 0, transformOrigin: 'top center' },
              {
                ease: 'none',
                scaleY: 1,
                scrollTrigger: {
                  ...triggerConfig,
                  scrub: 0.35,
                },
              },
            )
          : undefined

        const stageTrigger = ScrollTrigger.create({
          ...triggerConfig,
          fastScrollEnd: true,
          onUpdate: (self) => {
            const stage = resolveStage(self.progress)
            if (stage !== currentStage) {
              applyStage(stage)
            }
          },
        })

        window.addEventListener('resize', handleTimelineResizeFrame)
        ScrollTrigger.refresh()

        return () => {
          window.removeEventListener('resize', handleTimelineResizeFrame)
          progressTween?.scrollTrigger?.kill()
          progressTween?.kill()
          stageTrigger.kill()
        }
      })

      return () => mm.revert()
    },
    { scope: scrollingBullets },
  )

  return (
    <section
      ref={scrollingBullets}
      className="page-section scrolling-bullets bg-brand-dark"
    >
      <div className="page-section__inner">
        <h2 className="scrolling-bullets-header heading-xxl text-inverse">
          Get insights
          <br />
          <span className="text-brand">without waiting in line.</span>
        </h2>

        <div className="row">
          <div className="column-left">
            <div className="vertical-progress-timeline">
              <div className="timeline">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="3"
                  height="1301"
                  viewBox="0 0 3 1301"
                  fill="none"
                >
                  <defs>
                    <clipPath
                      id="scrolling-bullets-progress-clip"
                      clipPathUnits="userSpaceOnUse"
                    >
                      <rect
                        className="animated-mask"
                        x="0"
                        y="0"
                        width="3"
                        height="1301"
                      />
                    </clipPath>
                  </defs>
                  <path
                    opacity="0.5"
                    d="M1.49982 1.5L1.50287 1298.67"
                    stroke="#FFA6DD"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="0 20"
                  />
                  <path
                    clipPath="url(#scrolling-bullets-progress-clip)"
                    d="M1.49982 1.5L1.50287 1298.67"
                    stroke="#FF5FA2"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="0 20"
                  />
                </svg>
              </div>

              <div className="bullets">
                <div className="bullet passed">
                  <div className="dot"></div>
                  <div className="copy">
                    <h3 className="heading-l text-inverse">Ask</h3>
                    <p className="body-l text-inverse">
                      Chat like you’d ask a teammate.
                    </p>
                  </div>
                  <LazyWistiaPlayer mediaId="q7w1cwbp4v" />
                </div>

                <div className="bullet inactive">
                  <div className="dot"></div>
                  <div className="copy">
                    <h3 className="heading-l text-inverse">Refine</h3>
                    <p className="body-l text-inverse">
                      Filter, add fields, and calculate metrics.
                    </p>
                  </div>
                  <LazyWistiaPlayer mediaId="imysu96c3n" />
                </div>

                <div className="bullet inactive">
                  <div className="dot"></div>
                  <div className="copy">
                    <h3 className="heading-l text-inverse">Summarize</h3>
                    <p className="body-l text-inverse">
                      Get insights to inform decisions.
                    </p>
                  </div>
                  <LazyWistiaPlayer mediaId="s29r8gduw1" />
                </div>

                <div className="bullet inactive">
                  <div className="dot"></div>
                  <div className="copy">
                    <h3 className="heading-l text-inverse">Follow up</h3>
                    <p className="body-l text-inverse">
                      Ask another question. Context carries over.
                    </p>
                  </div>
                  <LazyWistiaPlayer mediaId="ccrxtawu5f" />
                </div>

                <div className="bullet inactive">
                  <div className="dot"></div>
                  <div className="copy">
                    <h3 className="heading-l text-inverse">Keep exploring</h3>
                    <p className="body-l text-inverse">
                      Start a workbook and use our full analytics UI.
                    </p>
                  </div>
                  <LazyWistiaPlayer mediaId="3932gu1ilx" />
                </div>
              </div>
            </div>
          </div>

          <div className="column-right">
            <div className="visual-asset-frame">
              <div className="visual-asset-placeholder">
                <div className="visual-asset">
                  {loadedDesktopVideoIndexes.has(0) && (
                    <WistiaPlayer
                      transparentLetterbox
                      mediaId="q7w1cwbp4v"
                      ref={(el) => {
                        wistiaPlayerRef.current[0] = el
                      }}
                      onCanPlay={(e) => handleVideoCanPlay(e, 0)}
                    />
                  )}
                </div>

                <div className="visual-asset hidden">
                  {loadedDesktopVideoIndexes.has(1) && (
                    <WistiaPlayer
                      transparentLetterbox
                      mediaId="imysu96c3n"
                      ref={(el) => {
                        wistiaPlayerRef.current[1] = el
                      }}
                      onCanPlay={(e) => handleVideoCanPlay(e, 1)}
                    />
                  )}
                </div>

                <div className="visual-asset hidden">
                  {loadedDesktopVideoIndexes.has(2) && (
                    <WistiaPlayer
                      transparentLetterbox
                      mediaId="s29r8gduw1"
                      ref={(el) => {
                        wistiaPlayerRef.current[2] = el
                      }}
                      onCanPlay={(e) => handleVideoCanPlay(e, 2)}
                    />
                  )}
                </div>

                <div className="visual-asset hidden">
                  {loadedDesktopVideoIndexes.has(3) && (
                    <WistiaPlayer
                      transparentLetterbox
                      mediaId="ccrxtawu5f"
                      ref={(el) => {
                        wistiaPlayerRef.current[3] = el
                      }}
                      onCanPlay={(e) => handleVideoCanPlay(e, 3)}
                    />
                  )}
                </div>

                <div className="visual-asset hidden">
                  {loadedDesktopVideoIndexes.has(4) && (
                    <WistiaPlayer
                      transparentLetterbox
                      mediaId="3932gu1ilx"
                      ref={(el) => {
                        wistiaPlayerRef.current[4] = el
                      }}
                      onCanPlay={(e) => handleVideoCanPlay(e, 4)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
